import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/utils/api';
import type { RSSSource, RSSArticle, RSSPreference } from '@/types/rss';

const RSS_KEY = 'rss';
const SOURCES_KEY = 'rss-sources';
const ARTICLES_KEY = 'rss-articles';
const PREFERENCES_KEY = 'rss-preferences';

// Recommended sources
export function useRecommendedSources(category?: string) {
  return useQuery({
    queryKey: [RSS_KEY, 'recommended', category],
    queryFn: async () => {
      const response = await api.get('/rss/recommended', { params: { category } });
      return response.data;
    },
  });
}

// Source categories
export function useSourceCategories() {
  return useQuery({
    queryKey: [RSS_KEY, 'categories'],
    queryFn: async () => {
      const response = await api.get('/rss/categories');
      return response.data;
    },
  });
}

// User sources
export function useRSSSources(status?: string) {
  return useQuery({
    queryKey: [SOURCES_KEY, status],
    queryFn: async () => {
      const response = await api.get('/rss/sources', { params: { status } });
      return response.data as RSSSource[];
    },
  });
}

// Add source
export function useAddRSSSource() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: {
      name: string;
      url: string;
      description?: string;
      category?: string;
      language?: string;
      tags?: string[];
    }) => {
      const response = await api.post('/rss/sources', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SOURCES_KEY] });
    },
  });
}

// Delete source
export function useDeleteRSSSource() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (sourceId: number) => {
      await api.delete(`/rss/sources/${sourceId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SOURCES_KEY] });
    },
  });
}

// Update source
export function useUpdateRSSSource() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<RSSSource> }) => {
      const response = await api.put(`/rss/sources/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SOURCES_KEY] });
    },
  });
}

// Manual fetch
export function useManualFetchRSS() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (sourceId: number) => {
      const response = await api.post(`/rss/sources/${sourceId}/fetch`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ARTICLES_KEY] });
      queryClient.invalidateQueries({ queryKey: [SOURCES_KEY] });
    },
  });
}

// Articles
export function useRSSArticles(params?: {
  source_id?: number;
  category?: string;
  status?: string;
  limit?: number;
  offset?: number;
}) {
  return useQuery({
    queryKey: [ARTICLES_KEY, params],
    queryFn: async () => {
      const response = await api.get('/rss/articles', { params });
      return response.data;
    },
  });
}

// Single article
export function useRSSArticle(articleId: number) {
  return useQuery({
    queryKey: [ARTICLES_KEY, articleId],
    queryFn: async () => {
      const response = await api.get(`/rss/articles/${articleId}`);
      return response.data as RSSArticle;
    },
    enabled: !!articleId,
  });
}

// Preferences
export function useRSSPreferences() {
  return useQuery({
    queryKey: [PREFERENCES_KEY],
    queryFn: async () => {
      const response = await api.get('/rss/preferences');
      return response.data as RSSPreference;
    },
  });
}

// Update preferences
export function useUpdateRSSPreferences() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: Partial<RSSPreference>) => {
      const response = await api.put('/rss/preferences', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PREFERENCES_KEY] });
    },
  });
}
